import React, { useEffect, useState, useCallback } from "react";
import { TrendingUp, Calendar, CreditCard, Search } from "lucide-react";
import { api } from "@/lib/api";
import Pagination from "@/components/Pagination";

export default function Expiring() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("today"); // today, tomorrow, date, 7d, 15d, 30d
  const [targetDate, setTargetDate] = useState("");
  
  const [page, setPage] = useState(1);
  const limit = 25;
  const [total, setTotal] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    let url = `/admin/reports/expiring?page=${page}&limit=${limit}&filter_type=${filterType}`;
    if (filterType === "date" && targetDate) {
      url += `&target_date=${targetDate}`;
    }
    api.get(url).then(r => {
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [page, limit, filterType, targetDate]);

  useEffect(() => { load(); }, [load]);

  // Reset page when filter changes
  useEffect(() => { setPage(1); }, [filterType, targetDate]);

  const daysLeft = (planEnd) => {
    if (!planEnd) return null;
    return Math.ceil((new Date(planEnd) - new Date()) / 86400000);
  };

  const pageCount = Math.max(1, Math.ceil(total / limit));
  const filterLabel = filterType === "today" ? "today" : filterType === "tomorrow" ? "tomorrow" : filterType === "date" ? "on selected date" : filterType === "expired" ? "that are expired" : `within ${filterType.replace('d', '')} days`;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <h3 className="font-heading font-bold text-2xl text-brand-nav">Expiring Subscriptions</h3>
        <div className="flex flex-wrap items-center gap-2">
          {["today", "tomorrow", "7d", "15d", "30d", "expired", "date"].map(d => (
            <button key={d} onClick={() => setFilterType(d)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg capitalize transition-colors ${filterType === d ? "bg-brand-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {d.replace('d', ' Days')}
            </button>
          ))}
          {filterType === "date" && (
            <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 outline-none" />
          )}
        </div>
      </div>

      {loading ? <p className="text-slate-400 text-sm">Loading…</p> : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">No subscribers expiring {filterLabel}.</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Mobile No</th>
                <th className="text-left px-4 py-3">DTH ID</th>
                <th className="text-left px-4 py-3">Plan End</th>
                <th className="text-left px-4 py-3">Days Left</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const d = daysLeft(r.plan_end);
                const isExp = r.status === 'expired' || (d !== null && d < 0);
                return (
                  <tr key={i} className={`border-t border-slate-100 ${isExp || (d !== null && d <= 7) ? "bg-red-50/50" : d !== null && d <= 15 ? "bg-amber-50/30" : ""}`}>
                    <td className="px-4 py-3 font-semibold text-brand-nav">{r.customer?.name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.customer?.mobile || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.connection?.dth_id || "—"}</td>
                    <td className="px-4 py-3 text-sm">{r.plan_end}</td>
                    <td className="px-4 py-3">
                      {isExp ? (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Expired</span>
                      ) : (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          d <= 7 ? "bg-red-100 text-red-700" : d <= 15 ? "bg-amber-100 text-amber-700" : "bg-yellow-100 text-yellow-700"
                        }`}>
                          {d}d
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} perPage={limit} />
        </div>
      )}
    </div>
  );
}
